import { AlertTriangle, Trash2 } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';

/**
 * ConfirmModal - Reusable confirmation modal component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Called when modal is closed/cancelled
 * @param {Function} props.onConfirm - Called when user confirms action
 * @param {string} props.title - Modal title
 * @param {string} props.message - Main message/question
 * @param {React.ReactNode} props.children - Optional content to display (e.g., item details)
 * @param {string} props.confirmText - Text for confirm button (default: "Delete")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {React.ReactNode} props.icon - Icon to display in header (default: AlertTriangle)
 * @param {React.ReactNode} props.confirmIcon - Icon for confirm button (default: Trash2, set to null to hide)
 * @param {'danger'|'warning'|'theme'} props.variant - Color variant (default: 'danger')
 *   - danger: red gradient (for deletions)
 *   - warning: amber/yellow gradient
 *   - theme: linkoo purple/pink gradient (for general confirmations)
 */
const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    children,
    confirmText,
    cancelText,
    icon: Icon = AlertTriangle,
    confirmIcon: ConfirmIcon = Trash2,
    variant = 'danger'
}) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    // Variant styles
    const variants = {
        danger: {
            header: 'bg-gradient-to-r from-red-500 to-red-600',
            button: 'bg-red-600 hover:bg-red-700',
            iconBg: 'bg-white/20'
        },
        warning: {
            header: 'bg-gradient-to-r from-amber-500 to-orange-500',
            button: 'bg-amber-600 hover:bg-amber-700',
            iconBg: 'bg-white/20'
        },
        theme: {
            header: 'bg-gradient-to-r from-linkoo-purple to-linkoo-pink',
            button: 'bg-gradient-to-r from-linkoo-purple to-linkoo-pink hover:opacity-90',
            iconBg: 'bg-white/20'
        }
    };

    const styles = variants[variant] || variants.danger;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className={`${styles.header} p-6 text-white`}>
                    <div className="flex items-center gap-3">
                        <div className={`p-2 ${styles.iconBg} rounded-full`}>
                            <Icon className="w-6 h-6" />
                        </div>
                        <h2 className="text-xl font-bold">{title}</h2>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {message && (
                        <p className="text-gray-800 font-medium mb-4">{message}</p>
                    )}
                    {children}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 flex justify-end gap-3 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        {cancelText || t.common?.cancel || 'Cancel'}
                    </button>
                    <button
                        onClick={onConfirm}
                        className={`px-6 py-2 ${styles.button} text-white rounded-lg font-medium transition-colors flex items-center gap-2`}
                    >
                        {ConfirmIcon && <ConfirmIcon className="w-4 h-4" />}
                        {confirmText || t.common?.delete || 'Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
